<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { AlertCircle } from 'lucide-vue-next'
  import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
  import { Checkbox } from '@/components/ui/checkbox'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables'
  import { useAiAssistantBeta, type FeatureId } from './useAiAssistantBeta'
  import heroImage from './hero.png'

  const { isModalOpen, isAdmin, isActivated, isLoading, closeModal, activate } =
    useAiAssistantBeta()
  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      title: 'Meet the new JUPUS AI Assistant (Beta)',
      subtitle: 'Now in beta — free for a limited time.',
      body: 'Let JUPUS handle the work for you — autonomously. The assistant beta can execute a limited number of tasks on its own or draft your first email autonomously. All suggestions are generated based on your past interactions on our platform.',
      skillsHeader: 'Please select the skills you want to activate',
      skillAssignLawFields: 'Assign law fields',
      skillAssignClerks: 'Assign clerks',
      skillSuggestEmail: 'Suggest the first client email',
      disclaimerLine1: 'This is a beta release so AI can make mistakes.',
      disclaimerLine2: 'You can change selected features or opt out anytime.',
      notNow: 'Not now',
      activate: 'Activate AI assistant',
      errorTitle: "Couldn't activate the AI assistant. Please try again."
    },
    de: {
      title: 'Der neue JUPUS AI Assistant (Beta)',
      subtitle: 'Jetzt in der Beta — für begrenzte Zeit kostenlos.',
      body: 'Lass JUPUS die Arbeit für dich erledigen — autonom. Die Assistant-Beta kann eine begrenzte Anzahl von Aufgaben eigenständig ausführen oder deine erste E-Mail autonom entwerfen. Alle Vorschläge basieren auf deinen bisherigen Interaktionen auf unserer Plattform.',
      skillsHeader: 'Bitte wähle die Funktionen aus, die du aktivieren möchtest',
      skillAssignLawFields: 'Rechtsgebiete zuweisen',
      skillAssignClerks: 'Sachbearbeiter zuweisen',
      skillSuggestEmail: 'Erste Mandanten-E-Mail vorschlagen',
      disclaimerLine1: 'Dies ist eine Beta-Version, daher kann die KI Fehler machen.',
      disclaimerLine2: 'Du kannst die ausgewählten Funktionen jederzeit ändern oder dich abmelden.',
      notNow: 'Nicht jetzt',
      activate: 'AI Assistant aktivieren',
      errorTitle: 'AI Assistant konnte nicht aktiviert werden. Bitte versuche es erneut.'
    }
  })

  const skills: ReadonlyArray<{ id: FeatureId; labelKey: string }> = [
    { id: 'assign_law_fields', labelKey: 'skillAssignLawFields' },
    { id: 'assign_clerks', labelKey: 'skillAssignClerks' },
    { id: 'suggest_first_client_email', labelKey: 'skillSuggestEmail' }
  ]

  const selectedFeatures = ref<FeatureId[]>(skills.map((s) => s.id))

  // Admin + not-yet-activated gate is enforced upstream (only admins see the modal at all
  // via shouldShowModalOnMount), but we mirror it here as defense-in-depth so any future
  // surface that opens the modal can't bypass the role check.
  const canActivate = computed(
    () =>
      isAdmin.value && !isActivated.value && !isLoading.value && selectedFeatures.value.length > 0
  )
  const onlyOneSelected = computed(() => selectedFeatures.value.length === 1)

  function isSelected(id: FeatureId) {
    return selectedFeatures.value.includes(id)
  }

  function isLastSelected(id: FeatureId) {
    return onlyOneSelected.value && isSelected(id)
  }

  function toggle(id: FeatureId, checked: boolean) {
    if (checked) {
      if (!isSelected(id)) selectedFeatures.value = [...selectedFeatures.value, id]
    } else {
      selectedFeatures.value = selectedFeatures.value.filter((f) => f !== id)
    }
  }

  function handleOpenChange(next: boolean) {
    // We only process close events here (next=false). The dialog is opened declaratively
    // via :open="isModalOpen", so next=true is a no-op. Also suppress close while a
    // request is in flight — otherwise an outside-click mid-activation would race with
    // the response and leave the user staring at a closed modal whose state hasn't settled.
    if (next || isLoading.value) return
    closeModal()
  }

  function handleNotNow() {
    if (isLoading.value) return
    closeModal()
  }

  async function handleActivate() {
    if (!canActivate.value) return
    try {
      await activate([...selectedFeatures.value])
    } catch {
      toast({
        variant: 'destructive',
        icon: AlertCircle,
        title: t('errorTitle')
      })
    }
  }

  // Both buttons share these overrides to match Figma:
  //   text-base / leading-6 / rounded-lg / px-4 / soft drop shadow
  const buttonClass =
    'h-10 rounded-lg px-4 text-base font-medium leading-6 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]'
</script>

<template>
  <Dialog
    :open="isModalOpen"
    @update:open="handleOpenChange"
  >
    <DialogContent
      :class="[
        `grid w-full max-w-[min(900px,calc(100vw-2rem))] grid-cols-1 gap-0 overflow-hidden border-0 bg-white
        p-0`,
        'md:grid-cols-[400px_1fr]',
        'shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] sm:rounded-2xl md:max-w-[900px]',
        '[&_[data-cy=dialogCloseBtn]]:hidden'
      ]"
    >
      <img
        :src="heroImage"
        alt=""
        aria-hidden="true"
        class="hidden h-full w-[400px] object-cover md:block md:rounded-l-2xl"
      />

      <div class="flex flex-col items-start justify-center gap-4 self-stretch p-12">
        <div class="flex flex-1 flex-col items-start gap-6 self-stretch">
          <div class="flex flex-col self-stretch">
            <DialogTitle class="text-xl font-semibold leading-8 tracking-normal text-[#1F2937]">
              {{ t('title') }}
            </DialogTitle>
            <p class="text-sm font-normal leading-5 text-[#6B7280]">{{ t('subtitle') }}</p>
          </div>

          <p class="self-stretch text-sm font-normal leading-5 text-[#1F2937]">
            {{ t('body') }}
          </p>

          <div class="flex flex-col items-start gap-2 self-stretch">
            <p class="self-stretch text-sm font-semibold leading-5 text-[#1F2937]">
              {{ t('skillsHeader') }}
            </p>
            <div class="flex flex-col items-start gap-1 self-stretch">
              <label
                v-for="skill in skills"
                :key="skill.id"
                class="inline-flex items-center gap-2"
                :class="isLastSelected(skill.id) ? 'cursor-not-allowed' : 'cursor-pointer'"
              >
                <Checkbox
                  :checked="isSelected(skill.id)"
                  :disabled="isLoading || isLastSelected(skill.id)"
                  @update:checked="(v: boolean) => toggle(skill.id, v)"
                />
                <span class="text-sm font-normal leading-5 text-[#1F2937]">
                  {{ t(skill.labelKey) }}
                </span>
              </label>
            </div>
          </div>

          <p class="text-xs font-normal leading-4 text-[#6B7280]">
            {{ t('disclaimerLine1') }}
            <br />
            {{ t('disclaimerLine2') }}
          </p>
        </div>

        <div class="flex items-center justify-end gap-2 self-stretch">
          <Button
            variant="secondary"
            :class="[buttonClass, 'text-[#1F2937]']"
            :disabled="isLoading"
            @click="handleNotNow"
          >
            {{ t('notNow') }}
          </Button>
          <Button
            :class="buttonClass"
            :disabled="!canActivate"
            @click="handleActivate"
          >
            {{ t('activate') }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
